import { auth } from 'enablex/shared'
import { ChatUserAuth } from 'common/typings'

import api from '~/app/api'
import history from '~/app/history'

export const WORKSPACE_KEY = 'enx/workspace'
export const CHAT_USER_AUTH_KEY = 'enx/chat_user_auth'
const USER_ROLE = 'enx/channel-web/user-role'
const HOME_ROUTE = '/home'
const root = document.documentElement
async function fetchWorkspaceBranding(workspaceName) {
  const axios = api.getSecured({ autoLogin: true })
  let themeColor = '#1d6c90'
  try {
    const response = await axios.get(`/admin/user/workspaces/${workspaceName}`)
    if (response.data && response.data.branding) {
      // For super admin
      const branding = {...response.data.branding, parentDomain: response.data.parentDomain}
      localStorage.setItem('enx/branding', JSON.stringify(branding))
      themeColor = branding.themeColor
    } else if (response.data[0] && response.data[0].branding) {
      // For other users
      const branding = {...response.data[0].branding, parentDomain: response.data.parentDomain}
      localStorage.setItem('enx/branding', JSON.stringify(branding))
      themeColor = branding.themeColor
    } else {
      localStorage.removeItem('enx/branding')
    }

    root.style.setProperty('--theme-color', themeColor)
  } catch (err) {
    console.error(err)
    root.style.setProperty('--theme-color', themeColor)
  }
}

export async function setActiveWorkspace(workspaceName) {
  if (workspaceName) {
    // set workspace ID
    localStorage.setItem(WORKSPACE_KEY, workspaceName)

    // set branding for workspace
    // const axios = api.getSecured({ autoLogin: true })
    // let themeColor = '#1d6c90'
    // axios.get(`/admin/user/workspaces/${workspaceName}`).then(response => {
    //   if (response.data && response.data.branding) {
    //     //for super admin
    //     const branding = response.data.branding
    //     localStorage.setItem('enx/branding', JSON.stringify(branding))
    //     themeColor = branding.themeColor
    //   } else if (response.data[0] && response.data[0].branding) {
    //     //for other users
    //     const branding = response.data[0].branding
    //     localStorage.setItem('enx/branding', JSON.stringify(branding))
    //     themeColor = branding.themeColor
    //   } else {
    //     localStorage.removeItem('enx/branding')
    //   }
    //   root.style.setProperty('--theme-color', themeColor)
    // }).catch(err => {
    //   console.error(err)
    //   root.style.setProperty('--theme-color', themeColor)
    // })
    await fetchWorkspaceBranding(workspaceName)
  } else {
    localStorage.removeItem(WORKSPACE_KEY)
    localStorage.removeItem('enx/branding')
  }
}

export function getActiveWorkspace() {
  return localStorage.getItem(WORKSPACE_KEY)
}

export function getCurrentRole() {
  return localStorage.getItem(USER_ROLE)
}

export function setChatUserAuth(auth?: ChatUserAuth) {
  auth ? localStorage.setItem(CHAT_USER_AUTH_KEY, JSON.stringify(auth)) : localStorage.removeItem(CHAT_USER_AUTH_KEY)
}

export function getChatUserAuth(): ChatUserAuth | undefined {
  try {
    return JSON.parse(localStorage.getItem(CHAT_USER_AUTH_KEY) || '')
  } catch (err) {
    return undefined
  }
}

interface LoginCredentials {
  email: string
  password: string
  newPassword?: string
}

export default class BasicAuthentication {
  login = async (credentials: LoginCredentials, loginUrl: string, returnTo?: string) => {
    if (this.isAuthenticated()) {
      return
    }

    const { data } = await api
      .getAnonymous({ toastErrors: false })
      .post(`/admin/auth${loginUrl}`, credentials, { timeout: 15000 })

    auth.setToken(data.payload)

    await this.afterLoginRedirect(returnTo)
  }

  autoLogin = async (payload, returnTo, workspaceId) => {

    auth.setToken(payload)
    try {
      const axios = api.getSecured({ autoLogin: true })
      const response = await axios.get('/admin/user/workspaces')
      const workspaces = response?.data
      const filtered_ws = workspaces.filter(x => x.enabled === true)
      if (!filtered_ws || !filtered_ws.length) {
        return history.replace({ pathname: '/notAccessibleWorkspace' })
      }
      const workspaceExists = filtered_ws.some(workspace => workspace.workspace === workspaceId)
      if (workspaceExists) {
        await setActiveWorkspace(workspaceId || workspaces[0].workspace)
      } else {
        return history.replace({ pathname: '/notAccessibleWorkspace' })
      }
      window.location.href = returnTo
      return history.push(returnTo)
    } catch (err) {
      return history.replace({ pathname: '/notAccessibleWorkspace' })
    }
  }

  afterLoginRedirect = async (redirectTo?: string) => {
    // Chat user authentication triggers an event & auto-closes, so must be cleared from storage after.
    const chatUserAuth = getChatUserAuth()
    if (chatUserAuth) {
      try {
        const { data: workspaceId } = await api.getSecured().post('/admin/auth/me/chatAuth', chatUserAuth)
        await setActiveWorkspace(workspaceId)

        return history.replace('/chatAuthResult')
      } catch (error) {
        return history.replace({ pathname: '/chatAuthResult', state: { error: error.message } })
      } finally {
        setChatUserAuth(undefined)
      }
    }

    const { data: workspaces } = await api.getSecured().get('/admin/user/workspaces')
    if (!workspaces || !workspaces.length) {
      return history.replace('/noAccess')
    }

    // We set either the active workspace, or the first in the list he's allowed otherwise.
    await setActiveWorkspace(getActiveWorkspace() || workspaces[0].workspace)

    history.replace(redirectTo || HOME_ROUTE)
  }

  register = async ({ email, password }, registerUrl) => {
    if (this.isAuthenticated()) {
      return
    }

    const { data } = await api.getAnonymous({ toastErrors: false }).post(`/admin/auth${registerUrl}`, {
      email,
      password
    })

    auth.setToken(data.payload)
    await this.afterLoginRedirect()

    history.replace(HOME_ROUTE)
  }

  logout = async () => {
    const axios = api.getSecured({ useV1: false })
    const role = localStorage.getItem(USER_ROLE)
    if (role === 'agent' || role === 'supervisor') {
      try {
        const data = await axios.post('/admin/user/profile/offline')
      } catch (err) {
        console.error('Error while notifying server:', err)
      }
    }
    localStorage.removeItem(WORKSPACE_KEY)
    auth.logout(() => api.getSecured())
  }

  isAuthenticated() {
    return auth.isTokenValid()
  }
}
